import React from 'react';
import { Rnd } from 'react-rnd';
import RotateHandle from './RotateHandle';
import LayoutElementIcon from './LayoutElementIcon';
import { ELEMENT_LABELS, ELEMENT_LOCK_ASPECT, isPartitionType } from '../../utils/layoutElementTypes';

const MIN_SIZE = 20;

const getPartitionStyle = (type, color) => {
  const lineColor = color || '#9a9a9a';
  const base = { width: '100%', height: '100%', boxSizing: 'border-box' };
  switch (type) {
    case 'partition_dotted_line':
      return { ...base, borderTop: `4px dotted ${lineColor}` };
    case 'partition_solid_line':
      return { ...base, borderTop: `4px solid ${lineColor}` };
    case 'partition_circle':
      return { ...base, border: `3px solid ${lineColor}`, borderRadius: '50%' };
    case 'partition_semi_circle':
      return { ...base, border: `3px solid ${lineColor}`, borderBottom: 'none', borderRadius: '50% 50% 0 0' };
    case 'partition_square':
    case 'partition_rectangle':
    default:
      return { ...base, border: `3px solid ${lineColor}` };
  }
};

/**
 * `readOnly` is used by FloorPlanDashboardPage's order-taking view: elements still render at
 * their saved position/rotation, but aren't draggable/resizable/selectable there — they're
 * decorative context (doors/rooms/decor/partitions/equipment), not something staff interact with
 * when picking a table.
 */
const LayoutElementShape = ({
  element,
  isSelected,
  isSoleSelection = true,
  onSelect,
  onChange,
  readOnly = false,
  canvasScale = 1,
}) => {
  const isPartition = isPartitionType(element.type);
  const lockAspect = ELEMENT_LOCK_ASPECT.has(element.type);

  const handleDragStop = (e, d) => {
    // Clamped only after the drop, not during it - see TableShape.js's handleDragStop for why.
    onChange(element.id, { position_x: Math.max(0, Math.round(d.x)), position_y: Math.max(0, Math.round(d.y)) });
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    onChange(element.id, {
      width: Math.round(ref.offsetWidth),
      height: Math.round(ref.offsetHeight),
      position_x: Math.round(position.x),
      position_y: Math.round(position.y),
    });
  };

  const handleRotate = (angle) => {
    onChange(element.id, { rotation: angle });
  };

  const displayLabel = element.label || ELEMENT_LABELS[element.type] || element.type;

  return (
    <Rnd
      // An element saved without an explicit size comes back null from the API - passing that
      // straight through crashes react-rnd/re-resizable internally (it calls .toString() on
      // the raw value with no null guard of its own), taking down the whole dashboard render.
      size={{ width: element.width ?? MIN_SIZE, height: element.height ?? MIN_SIZE }}
      position={{ x: element.position_x, y: element.position_y }}
      lockAspectRatio={lockAspect}
      minWidth={MIN_SIZE}
      minHeight={MIN_SIZE}
      // See TableShape.js for why the editor drops bounds entirely rather than using "parent" or
      // a partial {left,top} object (react-rnd's `bounds` prop only accepts a selector
      // string/Element - an object is silently ignored, clamping every drag to the origin).
      bounds={readOnly ? 'parent' : undefined}
      scale={canvasScale}
      disableDragging={readOnly}
      enableResizing={!readOnly}
      onDragStop={readOnly ? undefined : handleDragStop}
      onResizeStop={readOnly ? undefined : handleResizeStop}
      onMouseDown={readOnly ? undefined : (e) => {
        e.stopPropagation();
        onSelect(element.id, e);
      }}
      className={`layoutElementShape${!readOnly && isSelected ? ' selected' : ''}${readOnly ? ' readOnly' : ''}`}
    >
      <div
        className="layoutElementRotator"
        style={{ transform: `rotate(${element.rotation || 0}deg)`, color: element.color || undefined }}
      >
        {isPartition ? (
          <div style={getPartitionStyle(element.type, element.color)} />
        ) : (
          <LayoutElementIcon type={element.type} />
        )}
      </div>
      {!isPartition && <div className="layoutElementLabel">{displayLabel}</div>}

      {!readOnly && isSelected && isSoleSelection && <RotateHandle onRotate={handleRotate} />}
    </Rnd>
  );
};

export default LayoutElementShape;
